import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, GripVertical, Power, Repeat, Search, ListChecks, FileSignature } from 'lucide-react';
import { campiAPI, schedeAPI } from '../services/api';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import { useAuthStore } from '../store/useStore';
import '../styles/Catalogo.css';

/**
 * SchedeCliniche — le schede della struttura.
 *
 * DUE PERMESSI DIVERSI SULLA STESSA SCHERMATA
 * L'amministrazione compone: sezioni, campi, ordine, obbligatorieta'.
 * La segreteria gestisce l'anagrafica: attivare, disattivare, ordinare,
 * assegnare a un'area — e basta, perche' un campo clinico con la sua
 * unita' di misura e' una decisione clinica, non organizzativa.
 *
 * Qui la differenza si vede (la segreteria non trova i pulsanti di
 * composizione), ma NON e' qui che viene applicata: ogni rotta di
 * scrittura la verifica da se'. Nascondere una voce non e' protezione.
 *
 * ATTIVARE UNA SCHEDA CONGELA I CAMPI che la compongono: da quel momento
 * tipo, unita' e limiti non si cambiano piu'. Lo dice il messaggio che
 * arriva dal server, non un avviso scritto qui, cosi' resta uno solo.
 */

const SchedeCliniche = () => {
    const ruolo = useAuthStore((s) => s.user?.ruolo);
    const navigate = useNavigate();
    const componi = ruolo === 'admin';

    const [schede, setSchede] = useState([]);
    const [aree, setAree] = useState([]);
    const [aperta, setAperta] = useState(null);
    const [catalogo, setCatalogo] = useState([]);
    const [cerca, setCerca] = useState('');
    const [areaFiltro, setAreaFiltro] = useState('');
    const [inSezione, setInSezione] = useState(null);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    /* L'errore della lettura sta per conto suo, con «Riprova»: la lettura
       riuscita lo toglie, quelli dei gesti restano in `errore`
       (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);
    const carica = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => schedeAPI.elenco());
            setSchede(r.data.data.schede);
            setErroreLettura(null);
        } catch (e) { setErroreLettura(motivoDellaLettura(e, 'Non riesco a leggere le schede')); }
    }, []);

    const caricaAree = useCallback(() => {
        conUnTentativo(() => campiAPI.aree()).then((r) => setAree(r.data.data.aree)).catch(() => {});
    }, []);
    useEffect(() => {
        carica();
        caricaAree();
    }, [carica, caricaAree]);

    const apri = async (id) => {
        setErrore(null);
        try {
            const r = await schedeAPI.dettaglio(id);
            setAperta(r.data.data.scheda);
        } catch (e) { setErrore('Non riesco ad aprire la scheda'); }
    };

    useEffect(() => {
        if (!inSezione) return;
        const t = setTimeout(async () => {
            try {
                const r = await campiAPI.elenco({
                    cerca: cerca || undefined, area: areaFiltro || undefined, stato: 'attivo',
                });
                setCatalogo(r.data.data.campi);
            } catch (e) { /* la ricerca che non torna non blocca la schermata */ }
        }, 250);
        return () => clearTimeout(t);
    }, [cerca, areaFiltro, inSezione]);

    /* Dove compare la scheda. Non e' un dettaglio di forma: una scheda di
       profilo dentro una procedura sono sette riquadri di anamnesi dentro
       una seduta, ed e' successo. */
    const spostaDove = async (s, valore) => {
        try {
            await schedeAPI.anagrafica(s.id, { dove: valore });
            carica();
            apri(s.id);
        } catch (e) {
            setErrore(e?.message || 'Non riesco a spostarla');
        }
    };

    /* L'ORDINE IN CUI LE SCHEDE COMPAIONO NEL PROFILO DEL PAZIENTE.
       La colonna, la rotta e l'ordinamento nel profilo esistevano da
       sempre; mancava il comando, e intanto questa schermata dichiarava
       «puoi attivarle, disattivarle e ordinarle». Una frase falsa a
       schermo e' un difetto.

       Si scambia l'ordine con la scheda vicina invece di far scrivere un
       numero: chi guarda un elenco pensa «questa va prima di quella»,
       non «questa vale 55». I numeri esistenti sono sparsi (10, 20, 51,
       66) e restano: si scambiano i due valori, e quando sono uguali -
       pelle e tricologia valgono 10 tutte e due - si usa il posto
       nell'elenco, che e' quello che si vede. */
    const sposta = async (s, verso) => {
        setErrore(null); setAvviso(null);
        const vicine = schede.filter((x) => x.dove === s.dove);
        const i = vicine.findIndex((x) => x.id === s.id);
        const vicina = vicine[i + verso];
        if (!vicina) return;
        const ordineDi = (x, posto) => (Number(x.ordine) === Number(s.ordine) ? posto : Number(x.ordine));
        try {
            await Promise.all([
                schedeAPI.anagrafica(s.id, { ordine: ordineDi(vicina, (Number(s.ordine) || 0) + verso) }),
                schedeAPI.anagrafica(vicina.id, { ordine: ordineDi(s, (Number(vicina.ordine) || 0) - verso) }),
            ]);
            carica();
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Non riesco a spostarla');
        }
    };

    /* Per quale sesso si compila (migration 235): lo decide solo
       l'amministrazione, con la rotta di modifica della scheda. */
    const cambiaSesso = async (s, valore) => {
        setErrore(null); setAvviso(null);
        try {
            await schedeAPI.modifica(s.id, { per_sesso: valore });
            carica();
            apri(s.id);
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a salvare per quale sesso vale'); }
    };

    const attiva = async (s, valore) => {
        setErrore(null); setAvviso(null);
        try {
            const r = await schedeAPI.anagrafica(s.id, { attiva: valore });
            if (r.data.message) setAvviso(r.data.message);
            carica();
            if (aperta?.id === s.id) apri(s.id);
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a cambiare lo stato'); }
    };

    const nuovaScheda = async () => {
        const nome = window.prompt('Nome della scheda nuova');
        if (!nome) return;
        const codice = window.prompt('Codice interno (minuscolo, senza spazi)',
            nome.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 30));
        if (!codice) return;
        try { await schedeAPI.crea({ codice, nome }); carica(); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a creare la scheda'); }
    };

    const nuovaSezione = async () => {
        const titolo = window.prompt('Titolo della sezione');
        if (!titolo) return;
        const rip = window.confirm(
            'È una sezione ripetibile?\n\nUna sezione ripetibile si compila più volte: tre interventi '
          + 'chirurgici, quattro farmaci. Serve quando prima si sarebbero scritti blocchi numerati a mano.\n\n'
          + 'OK = ripetibile, Annulla = normale');
        let etichetta;
        if (rip) {
            etichetta = window.prompt('Come si chiama una singola ripetizione? («Intervento», «Farmaco»)');
            if (!etichetta) return;
        }
        try {
            await schedeAPI.creaSezione(aperta.id, { titolo, ripetibile: rip, ripetibile_etichetta: etichetta });
            apri(aperta.id);
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a creare la sezione'); }
    };

    const aggiungi = async (campo) => {
        setErrore(null);
        try {
            await schedeAPI.aggiungiCampo(aperta.id, { sezione_id: inSezione, campo_id: campo.id });
            apri(aperta.id);
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco ad aggiungere il campo'); }
    };

    const togli = async (riga) => {
        try { await schedeAPI.togliCampo(riga.id); apri(aperta.id); }
        catch (e) { setErrore('Non riesco a togliere il campo'); }
    };

    const giaDentro = new Set((aperta?.sezioni || []).flatMap((z) => z.campi.map((c) => c.campo_id)));

    return (
        <div className="cat-pagina">
            <header className="cat-testa">
                <div>
                    <h1>Schede cliniche della struttura</h1>
                    <p>Una scheda non definisce campi: elenca campi del catalogo, in sezioni, in ordine.
                       {componi ? '' : ' Da qui puoi attivarle, disattivarle e spostarle su e giù: sezioni e campi li compone l’amministrazione.'}</p>
                </div>
                <div className="cat-testa-azioni">
                    {/* Il catalogo dei campi si raggiungeva solo dal riquadro
                        in Amministrazione. Ma i campi sono i mattoni delle
                        schede: chi ne cerca uno sta guardando una scheda, ed
                        e' qui che si ferma a chiedersi dove si aggiunge.
                        Il riquadro in Amministrazione resta.

                        Solo a chi compone: la rotta /campi e' riservata agli
                        amministratori, e `componi` e' gia' quel permesso. */}
                    {componi && (
                        <button className="btn-secondario" onClick={() => navigate('/campi')}
                                title="I campi che le schede elencano">
                            <ListChecks size={16} aria-hidden="true" /> Catalogo dei campi
                        </button>
                    )}
                    {/* La validazione stava solo in un riquadro di
                        Amministrazione. Ma la domanda «questi campi li ha
                        confermati un medico?» viene guardando una scheda:
                        e' qui che uno si ferma a chiederselo, non in
                        Amministrazione. Il riquadro la' resta.

                        Nessun `componi`: la schermata e' in sola lettura
                        per chi non e' amministrazione o segreteria, e
                        questa pagina la aprono gia' solo quei due ruoli. */}
                    <button className="btn-secondario" onClick={() => navigate('/validazione-campi')}
                            title="Chi firma i documenti che descrivono questi campi">
                        <FileSignature size={16} aria-hidden="true" /> Validazione dei campi
                    </button>
                    {componi && (
                        <button className="btn-primario" onClick={nuovaScheda}>
                            <Plus size={16} aria-hidden="true" /> Scheda nuova
                        </button>
                    )}
                </div>
            </header>

            {avviso && <p className="cat-avviso">{avviso} <button onClick={() => setAvviso(null)}>×</button></p>}
            <ErroreLettura testo={erroreLettura} classe="cat-errore"
                onRiprova={() => { carica(); if (!aree.length) caricaAree(); }} />
            {errore && <p className="cat-errore">{errore}</p>}

            <div className="cat-due">
                <aside className="cat-elenco">
                    {schede.map((s, i) => (
                        <div key={s.id} className={`cat-voce-riga ${aperta?.id === s.id ? 'aperta' : ''}`}>
                        <div className="cat-ordina">
                            <button type="button" title="Più in alto nel profilo del paziente"
                                    disabled={i === 0 || schede[i - 1].dove !== s.dove}
                                    onClick={() => sposta(s, -1)}>▲</button>
                            <button type="button" title="Più in basso nel profilo del paziente"
                                    disabled={i === schede.length - 1 || schede[i + 1].dove !== s.dove}
                                    onClick={() => sposta(s, +1)}>▼</button>
                        </div>
                        <button className={`cat-voce ${aperta?.id === s.id ? 'aperta' : ''}`}
                                onClick={() => apri(s.id)}>
                            <div>
                                <strong>{s.nome}</strong>
                                <span className="cat-meta">
                                    {s.area_nome || 'senza area'} · {s.sezioni} sezioni · {s.campi} campi
                                    {Number(s.varianti) > 0 && ` · ${s.varianti} versioni dei medici`}
                                </span>
                                <span className="cat-dove">
                                    {s.dove === 'procedura' ? 'nelle procedure' : 'nel profilo del paziente'}
                                    {s.per_sesso && s.per_sesso !== 'tutti' ? ` · solo sesso ${s.per_sesso}` : ''}
                                </span>
                            </div>
                            <span className={`cat-stato ${s.attiva ? 'attiva' : ''}`}>
                                {s.attiva ? 'attiva' : 'inattiva'}
                            </span>
                        </button>
                        </div>
                    ))}
                    {schede.length === 0 && <p className="cat-vuoto">Nessuna scheda.</p>}
                </aside>

                <section className="cat-dettaglio">
                    {!aperta ? <p className="cat-vuoto">Scegli una scheda a sinistra.</p> : (
                        <>
                            <div className="cat-testa-scheda">
                                <div>
                                    <h2>{aperta.nome}</h2>
                                    <span className="cat-meta">{aperta.area_nome || 'senza area'}</span>
                                </div>
                                <div className="cat-azioni">
                                    <label className="cat-dove-scelta">
                                        Compare
                                        <select value={aperta.dove || 'profilo'}
                                                onChange={(e) => spostaDove(aperta, e.target.value)}>
                                            <option value="profilo">nel profilo del paziente</option>
                                            <option value="procedura">dentro le procedure</option>
                                        </select>
                                    </label>
                                    <label className="cat-dove-scelta">
                                        Per quale sesso
                                        <select value={aperta.per_sesso || 'tutti'}
                                                onChange={(e) => cambiaSesso(aperta, e.target.value)}
                                                disabled={!componi}
                                                title={componi ? '' : 'Lo decide l’amministrazione'}>
                                            <option value="tutti">tutti</option>
                                            <option value="femminile">solo femminile</option>
                                            <option value="maschile">solo maschile</option>
                                        </select>
                                    </label>
                                    <button className={aperta.attiva ? 'btn-no' : 'btn-si'}
                                            onClick={() => attiva(aperta, !aperta.attiva)}>
                                        <Power size={15} aria-hidden="true" />
                                        {aperta.attiva ? 'Disattiva' : 'Attiva'}
                                    </button>
                                    {componi && (
                                        <button className="btn-secondario" onClick={nuovaSezione}>
                                            <Plus size={15} aria-hidden="true" /> Sezione
                                        </button>
                                    )}
                                </div>
                            </div>

                            {aperta.sezioni.length === 0 && (
                                <p className="cat-vuoto">
                                    Nessuna sezione. {componi ? 'Creane una per cominciare a comporre.'
                                                             : 'La compone l’amministrazione.'}
                                </p>
                            )}

                            {aperta.sezioni.map((z) => (
                                <div key={z.id} className="cat-sezione">
                                    <h3>
                                        {z.titolo}
                                        {z.ripetibile && (
                                            <span className="cat-tag" title={
                                                'Si compila una volta per ogni ' + z.ripetibile_etichetta.toLowerCase()}>
                                                <Repeat size={12} aria-hidden="true" /> per {z.ripetibile_etichetta.toLowerCase()}
                                            </span>
                                        )}
                                        <span className="cat-meta">{z.campi.length} campi</span>
                                    </h3>
                                    <ul className="cat-campi">
                                        {z.campi.map((c) => (
                                            <li key={c.id}>
                                                <GripVertical size={14} className="cat-presa" aria-hidden="true" />
                                                <span className="cat-nome">{c.nome}</span>
                                                <span className="cat-meta">
                                                    {c.unita || c.analita_unita || ''}
                                                    {c.formula ? ' calcolato' : ''}
                                                    {c.da_anagrafica ? ' dall’anagrafica' : ''}
                                                    {Number(c.in_schede) > 1 ? ` · in ${c.in_schede} schede` : ''}
                                                </span>
                                                {componi && (
                                                    <button className="btn-icona" title="Togli dalla scheda"
                                                            onClick={() => togli(c)}>
                                                        <Trash2 size={14} aria-hidden="true" />
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                        {z.campi.length === 0 && <li className="cat-vuoto">Sezione vuota.</li>}
                                    </ul>
                                    {componi && (
                                        <button className="btn-testo" onClick={() => {
                                            setInSezione(z.id); setCerca(''); setAreaFiltro('');
                                        }}>
                                            <Plus size={14} aria-hidden="true" /> Aggiungi un campo dal catalogo
                                        </button>
                                    )}
                                </div>
                            ))}
                        </>
                    )}
                </section>
            </div>

            {inSezione && (
                <div className="cat-velo" onClick={(e) => { if (e.target === e.currentTarget) setInSezione(null); }}>
                    <div className="cat-riquadro">
                        <h2>Scegli dal catalogo</h2>
                        <p className="cat-nota">
                            Un campo può stare una volta sola in una scheda. Quelli già dentro sono segnati
                            e non si aggiungono di nuovo.
                        </p>
                        <div className="cat-filtri" data-non-trattiene>
                            <label className="cat-cerca">
                                <Search size={15} aria-hidden="true" />
                                <input autoFocus value={cerca} onChange={(e) => setCerca(e.target.value)}
                                       placeholder="Cerca per nome" />
                            </label>
                            <select value={areaFiltro} onChange={(e) => setAreaFiltro(e.target.value)}>
                                <option value="">Tutte le aree</option>
                                {aree.map((a) => <option key={a.id} value={a.codice}>
                                    {a.nome}{a.trasversale ? ' (trasversale)' : ''}</option>)}
                            </select>
                        </div>
                        <ul className="cat-scelta">
                            {catalogo.map((c) => {
                                const dentro = giaDentro.has(c.id);
                                return (
                                    <li key={c.id}>
                                        <button disabled={dentro} onClick={() => aggiungi(c)}>
                                            <span className="cat-nome">{c.nome}</span>
                                            <span className="cat-meta">
                                                {c.area_nome} · {c.tipo.replace('_', ' ')}
                                                {c.unita ? ` · ${c.unita}` : ''}
                                                {Number(c.in_schede) > 0 ? ` · già in ${c.in_schede} schede` : ''}
                                            </span>
                                            {dentro && <span className="cat-tag">già in questa scheda</span>}
                                        </button>
                                    </li>
                                );
                            })}
                            {catalogo.length === 0 && <li className="cat-vuoto">Nessun campo trovato.</li>}
                        </ul>
                        <div className="cat-pulsanti">
                            <button className="btn-annulla" onClick={() => setInSezione(null)}>Chiudi</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchedeCliniche;
